import { Form, Input, Button, ConfigProvider } from "antd";
import { useForm } from "antd/es/form/Form";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useLogin } from "hooks/mutations/auth";
import { LoginForm } from "shared/types";
import { useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { FULL_VIEWPORT_HEIGHT, textPx } from "shared/constants/zoom";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import NewLogo from "../../assets/spott-logo.svg";
import { authFormTheme, AuthHeading, AuthSub, FieldLabel } from "./AuthUi";
import PhoneInput, { phoneRules } from "shared/components/PhoneInput";
import LangToggle from "components/LangToggle.component";

/**
 * Login — split editorial layout (2026-06-12-admin-login-prototype): brand
 * hero with the display headline on the left, the form panel on the right.
 * Auth logic (useLogin mutation, validation rules, recaptcha container) is
 * unchanged; only the presentation moved to the design system.
 */
const LoginScreen = () => {
  const [form] = useForm();
  const { mutate: login, isPending } = useLogin();
  const navigate = useNavigate();

  const onFinish = (values: LoginForm) => {
    login(values);
  };

  return (
    <div style={{ display: "flex", minHeight: FULL_VIEWPORT_HEIGHT, background: colors.page }}>
      {/* ── Hero (left) ─────────────────────────────────────────────── */}
      <div
        style={{
          flex: 1.55,
          position: "relative",
          padding: "34px 56px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          overflow: "hidden",
          isolation: "isolate",
        }}
      >
        {/* Brand lockup: the Spott cube beside the wordmark, in the marketing
            site header's proportions (Inter Tight 600, -0.045em, ink). */}
        <div
          style={{
            position: "absolute",
            top: 34,
            left: 56,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <img src={NewLogo} alt="" aria-hidden="true" style={{ width: 40, height: 40, display: "block" }} />
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 600,
              fontSize: textPx(29),
              letterSpacing: "-0.045em",
              lineHeight: 1,
              color: colors.ink,
            }}
          >
            spott
          </span>
        </div>

        <div
          style={{
            fontSize: textPx(11),
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            color: colors.ink3,
            marginBottom: 18,
          }}
        >
          {translate("title.login_welcome")}
        </div>
        <h1
          style={{
            margin: 0,
            fontFamily: "var(--font-display)",
            fontSize: "clamp(80px, 9vw, 112px)",
            lineHeight: 0.95,
            letterSpacing: "-0.04em",
            fontWeight: 600,
            color: colors.ink,
          }}
        >
          {translate("title.login_hero_1")}
          <br />
          <span style={{ color: colors.navy }}>{translate("title.login_hero_2")}</span>
        </h1>

        {/* Soft navy radial glow, bottom-right */}
        <div
          style={{
            position: "absolute",
            bottom: -120,
            right: -40,
            width: 520,
            height: 520,
            zIndex: -1,
            background: `radial-gradient(circle, ${colors.navy}29 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />
      </div>

      {/* ── Form panel (right) — fixed width, form block centered ───── */}
      <div
        style={{
          width: 520,
          flexShrink: 0,
          position: "relative",
          background: colors.card,
          borderLeft: `1px solid ${colors.line}`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "40px 48px",
        }}
      >
        {/* Language toggle in the panel's top-right corner — the page's far
            right, same slot as the client and instructor login screens. */}
        <div style={{ position: "absolute", top: 24, right: 24 }}>
          <LangToggle />
        </div>
        <ConfigProvider theme={authFormTheme}>
          <div style={{ width: "100%", maxWidth: 400 }}>
            <AuthHeading>{translate("button.login")}</AuthHeading>
            <AuthSub>{translate("subtitle.login")}</AuthSub>

            <Form form={form} name="login" layout="vertical" requiredMark={false} onFinish={onFinish} autoComplete="off">
              <Form.Item
                name="phone_number"
                label={<FieldLabel>{translate("label.phone_number")}</FieldLabel>}
                rules={phoneRules({ smsOtp: true })}
              >
                <PhoneInput variant="inline" smsOtp placeholder="812 345 6789" />
              </Form.Item>
              <Form.Item
                name="password"
                label={<FieldLabel>{translate("label.password")}</FieldLabel>}
                style={{ marginBottom: 10 }}
                rules={[
                  {
                    required: true,
                    message: `${translate("form.error.input")} ${translate("label.password")} ${translate("form.error.input_end")}`,
                  },
                ]}
              >
                <Input.Password placeholder="••••••••" />
              </Form.Item>

              <div style={{ textAlign: "right", marginBottom: 8 }}>
                <span
                  onClick={() => navigate(PATHS.FORGET_PASSWORD)}
                  style={{ fontSize: textPx(12.5), fontWeight: 500, color: colors.ink2, cursor: "pointer" }}
                >
                  {translate("button.forget_password")}
                </span>
              </div>

              <div id="recaptcha-container" />
              <Form.Item style={{ marginTop: 10, marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" loading={isPending} style={{ width: "100%" }}>
                  {translate("button.login")} <ArrowRightOutlined style={{ fontSize: textPx(14) }} />
                </Button>
              </Form.Item>
            </Form>
          </div>
        </ConfigProvider>
      </div>
    </div>
  );
};

export default LoginScreen;
